import { Spinner } from '@/components/ui/spinner';
import { lazy, Suspense } from 'react';
import { Navigate, BrowserRouter, Route, Routes, useLocation } from 'react-router-dom';

import { LoginPage } from './features/auth/LoginPage';
import { ForgotPasswordPage } from './features/auth/ForgotPasswordPage';
import { ResetPasswordPage } from './features/auth/ResetPasswordPage';
import { RegisterPage } from './features/auth/RegisterPage';
import { RequireAuth } from './features/auth/RequireAuth';
import { useEffect } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { useDocumentHead } from './hooks/useDocumentHead';
import { markIndexability, type DocumentHead } from './lib/document-head';
import { PUBLIC_HEADS } from './lib/public-heads';
import { useAuthStore } from './stores/useAuthStore';
import { LandingPage } from './features/landing/LandingPage';
import { LegalLayout, PrivacyPolicyPage, TermsPage } from './features/legal/LegalPages';
import { AcceptInvitationPage } from './features/members/AcceptInvitationPage';

/**
 * 後台頁面全部 lazy load。
 *
 * ## 為什麼值得做
 *
 * 量過了：entry chunk 1,717,872 bytes 裡有 **784,371（45.7%）是 landing 訪客
 * 證明執行不到的後台程式碼**，而 landing 自己只佔 13,003（0.8%）。
 * 在 Slow 4G 上那個 chunk 下載了 4.46 秒——一個只是來看看這是什麼產品的人，
 * 下載了整個後台。
 *
 * ## 為什麼是一個共用的 Suspense，不是每條路由各包一個
 *
 * 每條包一個要寫 39 次同樣的 fallback，而且下一個加路由的人一定會忘。
 * 包在 `RequireAuth` 外層一次到位——那底下**每一頁都需要登入**，
 * 本來就是同一組人、同一種載入體驗。
 */
/**
 * 後台的殼層（側欄、頂欄、佈局）。訪客一頁都看不到，沒有理由讓他下載。
 * default export，所以不走 `lazyPage`。
 */
const App = lazy(() => import('./App'));

const lazyPage = <T extends string>(
  loader: () => Promise<Record<T, React.ComponentType>>,
  name: T,
) => lazy(async () => ({ default: (await loader())[name] }));

const AgentEvalPage = lazyPage(() => import('./features/agent/AgentEvalPage'), 'AgentEvalPage');
const AiSettingsPage = lazyPage(() => import('./features/ai/AiSettingsPage'), 'AiSettingsPage');
const ApiTokensPage = lazyPage(
  () => import('./features/api-tokens/ApiTokensPage'),
  'ApiTokensPage',
);
const BillingPage = lazyPage(() => import('./features/billing/BillingPage'), 'BillingPage');
const BroadcastReportPage = lazyPage(
  () => import('./features/broadcasts/BroadcastReportPage'),
  'BroadcastReportPage',
);
const BroadcastsPage = lazyPage(
  () => import('./features/broadcasts/BroadcastsPage'),
  'BroadcastsPage',
);
const ChannelsPage = lazyPage(() => import('./features/channels/ChannelsPage'), 'ChannelsPage');
const CommentRulesPage = lazyPage(
  () => import('./features/automation/CommentRulesPage'),
  'CommentRulesPage',
);
const ContactFieldsPage = lazyPage(
  () => import('./features/contact-fields/ContactFieldsPage'),
  'ContactFieldsPage',
);
const CouponsPage = lazyPage(() => import('./features/coupons/CouponsPage'), 'CouponsPage');
const FlowsPage = lazyPage(() => import('./features/flows/FlowsPage'), 'FlowsPage');
const HomePage = lazyPage(() => import('./features/home/HomePage'), 'HomePage');
const InboxPage = lazyPage(() => import('./features/inbox/InboxPage'), 'InboxPage');
const JourneyEditorPage = lazyPage(
  () => import('./features/journeys/JourneyEditorPage'),
  'JourneyEditorPage',
);
const JourneysPage = lazyPage(() => import('./features/journeys/JourneysPage'), 'JourneysPage');
const KeywordRulesPage = lazyPage(
  () => import('./features/automation/KeywordRulesPage'),
  'KeywordRulesPage',
);
const KnowledgePage = lazyPage(() => import('./features/knowledge/KnowledgePage'), 'KnowledgePage');
const MembersPage = lazyPage(() => import('./features/members/MembersPage'), 'MembersPage');
const ProfilePage = lazyPage(() => import('./features/members/ProfilePage'), 'ProfilePage');
const RealtimeDebugPage = lazyPage(
  () => import('./features/realtime/RealtimeDebugPage'),
  'RealtimeDebugPage',
);
const RepConsolePage = lazyPage(() => import('./features/rep/RepConsolePage'), 'RepConsolePage');
const RichMenuEditorPage = lazyPage(
  () => import('./features/rich-menus/RichMenuEditorPage'),
  'RichMenuEditorPage',
);
const RichMenusPage = lazyPage(
  () => import('./features/rich-menus/RichMenusPage'),
  'RichMenusPage',
);
const RolesPage = lazyPage(() => import('./features/members/RolesPage'), 'RolesPage');
const SavedRepliesPage = lazyPage(
  () => import('./features/saved-replies/SavedRepliesPage'),
  'SavedRepliesPage',
);
const SegmentsPage = lazyPage(() => import('./features/segments/SegmentsPage'), 'SegmentsPage');
const StoresPage = lazyPage(() => import('./features/stores/StoresPage'), 'StoresPage');
const TagsPage = lazyPage(() => import('./features/tags/TagsPage'), 'TagsPage');
const WebhooksPage = lazyPage(() => import('./features/webhooks/WebhooksPage'), 'WebhooksPage');
const WheelCampaignsPage = lazyPage(
  () => import('./features/wheel/WheelCampaignsPage'),
  'WheelCampaignsPage',
);
const WorkspaceSettingsPage = lazyPage(
  () => import('./features/workspaces/WorkspaceSettingsPage'),
  'WorkspaceSettingsPage',
);

/**
 * flow 編輯器是唯一 lazy load 的頁面。
 *
 * `@xyflow/react` 解開來一百多萬位元組，而它**只有這一頁用得到**。後台不像 widget
 * 有 50KB 硬上限，但把它塞進主 bundle 等於讓每個只想看收件匣的人都下載一次畫布引擎。
 */
const FlowEditorPage = lazy(async () => ({
  default: (await import('./features/flows/FlowEditorPage')).FlowEditorPage,
}));

/** 範本庫也 lazy load：它的預覽用的是同一個畫布引擎。 */
const FlowTemplatesPage = lazy(async () => ({
  default: (await import('./features/flows/FlowTemplatesPage')).FlowTemplatesPage,
}));

/**
 * 報表也 lazy load：圖表引擎（recharts）只有這一頁與券活動成效用得到。
 *
 * 它在主 bundle 裡是純粹的浪費——大部分人整天待在收件匣，不會打開報表。
 */
const ReportsPage = lazy(async () => ({
  default: (await import('./features/reports/ReportsPage')).ReportsPage,
}));

/**
 * 路由表。
 *
 * 受保護的頁面全部包在 RequireAuth 底下，新增頁面時不用（也不該）自己再檢查一次登入。
 */
/**
 * 公開頁各自的 head。值一律取自 `PUBLIC_HEADS`——預渲染讀的是同一份。
 *
 * 在此之前 38 條路由共用 `index.html` 那一份——瀏覽器分頁全都長一樣，而搜尋引擎
 * 看到的是三個「標題與描述完全相同」的頁面。canonical 也是這裡才有：新舊網域
 * 並存（`.onrender.com` 那個主機名 Render 不給關，301 這條路不存在），
 * 沒有 canonical 就是兩份重複內容互相稀釋。
 *
 * 後台頁不設 canonical——它們本來就不該被索引（robots.txt 擋著）。
 */
function PublicHead({ head, children }: { head: DocumentHead; children: React.ReactNode }) {
  useDocumentHead(head);

  return <>{children}</>;
}

/**
 * 根網址（E5-02-02）：訪客看產品頁、登入的人直接進後台。
 * status 還在 'unknown'（session 還原中）時先不渲染——避免已登入的人閃一下 landing。
 */
function RootGate() {
  const { status, bootstrap } = useAuthStore(
    useShallow((state) => ({ status: state.status, bootstrap: state.bootstrap })),
  );

  // bootstrap 由進入點自己觸發（RequireAuth 的同一個模式）——訪客直接開根網址時
  // 沒有任何受保護頁面幫忙問過 session，不問的話 status 永遠停在 unknown
  useEffect(() => {
    if (status === 'unknown') {
      void bootstrap();
    }
  }, [status, bootstrap]);

  if (status === 'unknown') {
    return null;
  }

  return status === 'authenticated' ? (
    <Navigate to="/home" replace />
  ) : (
    <PublicHead head={PUBLIC_HEADS['/']}>
      <LandingPage />
    </PublicHead>
  );
}

/**
 * 白名單外的路徑掛上 noindex。
 *
 * 掛在 `AppRoutes` 最外層而不是逐頁加：真正需要的是**沒有人記得加的那些**
 * ——尤其 `/*` 這條 catch-all，它讓任何亂打的網址都回 200 HTML。
 */
function Indexability() {
  const { pathname } = useLocation();

  useEffect(() => {
    markIndexability(pathname);
  }, [pathname]);

  return null;
}

export function AppRoutes() {
  return (
    <>
      <Indexability />
      <Routes>
        {/* 根網址是產品，不是登入框（E5-02-02） */}
        <Route path="/" element={<RootGate />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
        {/*
        隱私政策與服務條款是 Meta App Review 的必要條件，審核方會在**未登入**的情況下
        打開它們——所以放在 RequireAuth 外面。
      */}
        <Route
          path="/privacy"
          element={
            <PublicHead head={PUBLIC_HEADS['/privacy']}>
              <LegalLayout>
                <PrivacyPolicyPage />
              </LegalLayout>
            </PublicHead>
          }
        />
        <Route
          path="/terms"
          element={
            <PublicHead head={PUBLIC_HEADS['/terms']}>
              <LegalLayout>
                <TermsPage />
              </LegalLayout>
            </PublicHead>
          }
        />
        <Route path="/register" element={<RegisterPage />} />
        <Route
          element={
            <Suspense fallback={<Spinner className="mx-auto mt-10 size-5 text-muted-foreground" />}>
              <RequireAuth />
            </Suspense>
          }
        >
          {/* 導購版（E4-06-04）：手機全幅，不吃 App 的側欄佈局 */}
          <Route path="/rep" element={<RepConsolePage />} />
          <Route element={<App />}>
            <Route path="/home" element={<HomePage />} />
            <Route path="/inbox" element={<InboxPage />} />
            <Route path="/settings/channels" element={<ChannelsPage />} />
            <Route path="/settings/tags" element={<TagsPage />} />
            <Route path="/settings/stores" element={<StoresPage />} />
            <Route path="/settings/billing" element={<BillingPage />} />
            <Route path="/settings/contact-fields" element={<ContactFieldsPage />} />
            <Route path="/settings/segments" element={<SegmentsPage />} />
            <Route path="/settings/coupons" element={<CouponsPage />} />
            <Route path="/settings/wheel" element={<WheelCampaignsPage />} />
            <Route path="/settings/journeys" element={<JourneysPage />} />
            <Route path="/settings/journeys/:journeyId" element={<JourneyEditorPage />} />
            <Route path="/settings/rich-menus" element={<RichMenusPage />} />
            <Route path="/settings/rich-menus/:menuId" element={<RichMenuEditorPage />} />
            <Route path="/settings/saved-replies" element={<SavedRepliesPage />} />
            <Route path="/settings/automation" element={<KeywordRulesPage />} />
            <Route path="/settings/comment-rules" element={<CommentRulesPage />} />
            <Route path="/settings/ai" element={<AiSettingsPage />} />
            <Route path="/settings/knowledge" element={<KnowledgePage />} />
            <Route path="/settings/ai-agent" element={<AgentEvalPage />} />
            <Route path="/settings/api-tokens" element={<ApiTokensPage />} />
            <Route path="/settings/webhooks" element={<WebhooksPage />} />
            <Route path="/broadcasts" element={<BroadcastsPage />} />
            <Route
              path="/reports"
              element={
                <Suspense
                  fallback={<Spinner className="mx-auto mt-10 size-5 text-muted-foreground" />}
                >
                  <ReportsPage />
                </Suspense>
              }
            />
            <Route path="/broadcasts/:broadcastId" element={<BroadcastReportPage />} />
            <Route path="/settings/flows" element={<FlowsPage />} />
            {/* 放在 :flowId 之前——templates 不是一個 flow id */}
            <Route
              path="/settings/flows/templates"
              element={
                <Suspense
                  fallback={<Spinner className="mx-auto mt-10 size-5 text-muted-foreground" />}
                >
                  <FlowTemplatesPage />
                </Suspense>
              }
            />
            <Route
              path="/settings/flows/:flowId"
              element={
                <Suspense
                  fallback={<Spinner className="mx-auto mt-10 size-5 text-muted-foreground" />}
                >
                  <FlowEditorPage />
                </Suspense>
              }
            />
            <Route path="/settings/members" element={<MembersPage />} />
            <Route path="/settings/roles" element={<RolesPage />} />
            <Route path="/settings/workspace" element={<WorkspaceSettingsPage />} />
            <Route path="/settings/profile" element={<ProfilePage />} />
            <Route path="/invitations/:token" element={<AcceptInvitationPage />} />
            <Route path="/debug/realtime" element={<RealtimeDebugPage />} />
            {/* 未知路徑落回首頁 */}
            <Route path="*" element={<HomePage />} />
          </Route>
        </Route>
      </Routes>
    </>
  );
}

export function AppRouter() {
  return (
    <BrowserRouter>
      <AppRoutes />
    </BrowserRouter>
  );
}
